import { useEffect, useState } from "react";
import { Save, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import type { QuestionSet } from "@/types/database";

export default function AdminSettingsPage() {
  const [activeSet, setActiveSet] = useState<QuestionSet | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("question_sets")
      .select("*")
      .eq("active", true)
      .maybeSingle();
    if (error) setError(error.message);
    setActiveSet((data ?? null) as QuestionSet | null);
  }
  useEffect(() => { void load(); }, []);

  async function save() {
    if (!activeSet) return;
    setBusy(true); setError(null);
    const { error } = await supabase.from("question_sets").update({
      questions_per_session: activeSet.questions_per_session,
      time_limit_per_question_sec: activeSet.time_limit_per_question_sec,
      total_time_limit_sec: activeSet.total_time_limit_sec,
      show_explanations_inline: activeSet.show_explanations_inline,
      allow_retry: activeSet.allow_retry,
      one_attempt_per_student: activeSet.one_attempt_per_student,
      unanswered_counts_as_incorrect: activeSet.unanswered_counts_as_incorrect,
      updated_at: new Date().toISOString(),
    }).eq("id", activeSet.id);
    if (error) setError(error.message);
    else setSavedAt(new Date().toLocaleString());
    setBusy(false);
  }

  async function resetLeaderboard() {
    if (!activeSet) return;
    if (!confirm("This marks all completed sessions for the active set as 'archived' so they don't appear on the leaderboard. Continue?")) return;
    setResetting(true);
    // Soft-reset: flip status to 'abandoned' so leaderboard_view (which filters status='completed') excludes them.
    await supabase
      .from("game_sessions")
      .update({ status: "abandoned", flag_reason: "leaderboard reset" })
      .eq("question_set_id", activeSet.id)
      .eq("status", "completed");
    setResetting(false);
    alert("Leaderboard reset for this question set.");
  }

  if (!activeSet) {
    return (
      <div className="pill-warn px-3 py-2 inline-flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> No active question set. Activate one in /admin/questions.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <header>
        <h1 className="font-display text-3xl text-white">Competition settings</h1>
        <p className="text-steel-300">Settings apply to the active set: <span className="text-white">{activeSet.title}</span></p>
      </header>

      {error && <div className="pill-bad px-3 py-2">{error}</div>}

      <div className="card-dark p-5 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Questions per game">
            <input type="number" className="input"
              value={activeSet.questions_per_session}
              onChange={(e) => setActiveSet({ ...activeSet, questions_per_session: Number(e.target.value) })} />
          </Field>
          <Field label="Per-question seconds">
            <input type="number" className="input"
              value={activeSet.time_limit_per_question_sec}
              onChange={(e) => setActiveSet({ ...activeSet, time_limit_per_question_sec: Number(e.target.value) })} />
          </Field>
          <Field label="Total seconds">
            <input type="number" className="input"
              value={activeSet.total_time_limit_sec}
              onChange={(e) => setActiveSet({ ...activeSet, total_time_limit_sec: Number(e.target.value) })} />
          </Field>
        </div>

        <Toggle label="Show explanations inline (after each answer)"
          checked={activeSet.show_explanations_inline}
          onChange={(v) => setActiveSet({ ...activeSet, show_explanations_inline: v })} />
        <Toggle label="Allow students to retry"
          checked={activeSet.allow_retry}
          onChange={(v) => setActiveSet({ ...activeSet, allow_retry: v })} />
        <Toggle label="One official attempt per student per set"
          checked={activeSet.one_attempt_per_student}
          onChange={(v) => setActiveSet({ ...activeSet, one_attempt_per_student: v })} />
        <Toggle label="Unanswered questions count as incorrect (otherwise: skipped)"
          checked={activeSet.unanswered_counts_as_incorrect}
          onChange={(v) => setActiveSet({ ...activeSet, unanswered_counts_as_incorrect: v })} />

        <div className="flex items-center justify-between">
          <div className="text-xs text-steel-400">{savedAt ? `Saved ${savedAt}` : ""}</div>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      <div className="card-dark p-5">
        <h2 className="font-display text-lg text-white mb-2">Reset leaderboard</h2>
        <p className="text-sm text-steel-300 mb-3">
          Archives existing completed sessions for this set so a new competition starts fresh.
        </p>
        <button className="btn-outline" onClick={resetLeaderboard} disabled={resetting}>
          {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reset
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><div className="label">{label}</div>{children}</label>;
}
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm">{label}</span>
      <input type="checkbox" className="accent-forge-500 w-5 h-5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}
